/**
 * This custom hook provides the functionality to save a user with the given parameters.
 */

import {useContext, useState} from "react";
import {FirebaseAuthContext} from "../../../../commons/firebase/FirebaseAuthProvider";
import {useHistory} from "react-router-dom";
import {useSnackbar} from "notistack";
import {useIntl} from "react-intl";

const useEditUser = () => {
    const [saveInProgress, setSaveInProgress] = useState(false)

    const {signedInUser} = useContext(FirebaseAuthContext);
    const history = useHistory();

    const {enqueueSnackbar} = useSnackbar();

    // Internationalization for simple strings
    const intl = useIntl();

    const saveUser = async (user) => {
        setSaveInProgress(true)

        const idToken = await signedInUser.getIdToken()
        return fetch('/reg-main/users/' + user.uid, {
            method: 'PUT',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({
                idToken: idToken,
                userData: user
            })
        })
            .then(async (data) => {
                // Throw an error including the textual status of the HTTP response code (no detailed message necessary)
                if (data.status !== 200) {
                    throw new Error((await data.json()).status)
                }

                enqueueSnackbar(
                    intl.formatMessage({
                        id: 'app.users.messages.edit.success',
                        defaultMessage: 'User {firstName} {secondName} saved.'
                    }, {firstName: user.firstName, secondName: user.secondName}), {
                        variant: 'success',
                        preventDuplicate: true,
                        autoHideDuration: 3000
                    });
                history.push('/admin/users');
            })
            .catch((error) => {
                enqueueSnackbar(
                    intl.formatMessage({
                        id: 'app.users.messages.edit.error',
                        defaultMessage: 'Something went wrong while saving the user: {error}'
                    }, {error: error.message}), {
                        variant: 'error',
                        preventDuplicate: true,
                        autoHideDuration: 5000
                    });
            }).finally(() => {
                setSaveInProgress(false)
            })
    }

    const cancel = () => {
        history.push('/admin/users');
    }

    /*
      Sends a password init mail to the email address with the related UID.
     */
    const resetPassword = async (user) => {
        const idToken = await signedInUser.getIdToken()

        return fetch('/reg-main/sendPasswordInitMail', {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({
                idToken: idToken,
                uid: user.uid
            })
        })
            .then(async (data) => {
                // Throw an error including the textual status of the HTTP response code (no detailed message necessary)
                if (data.status !== 200) {
                    throw new Error((await data.json()).status)
                }

                enqueueSnackbar(
                    intl.formatMessage({
                        id: 'app.users.messages.resetPassword.success',
                        defaultMessage: 'Password reset link sent to {email}.'
                    }, {email: user.email}), {
                        variant: 'success',
                        preventDuplicate: true,
                        autoHideDuration: 3000
                    });
            })
            .catch((error) => {
                enqueueSnackbar(
                    intl.formatMessage({
                        id: 'app.users.messages.resetPassword.error',
                        defaultMessage: 'Something went wrong while resetting the password: {error}'
                    }, {error: error.message}), {
                        variant: 'error',
                        preventDuplicate: true,
                        autoHideDuration: 5000
                    });
            });
    }

    return {saveUser, saveInProgress, cancel, resetPassword}
}
export default useEditUser;